import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const CLIPBOARD_SCORE_HISTORY_REQUEST_SIZE = 20;

export const clipboardScoreActionLabelMap = new Map<string, string>([
  ["WORK_SHIFT", "Worked Shift"],
  ["CANCEL_SHIFT", "Cancellation"],
  ["NO_CALL_NO_SHOW", "No Show"],
  ["CORRECT_SCORE", "Score Correction"],
  ["ACCOUNT_RE_ENROLL", "Account Re-enrolled"],
  ["START_SHIFT_LATE", "Shift Started Late"],
  ["CLOCKIN_TIMELINESS_BONUS", "Clocked in On Time"],
  ["QUALITY_BONUS", "Excellence Bonus"],
  ["CLIPBOARD_SCORE_LAUNCH", "Clipboard Score Launch"],
  ["WORKER_LEFT_EARLY_WITHOUT_PERMISSION", "Shift Abandonment"],
  ["ORIENTATION_PASSED", "Demo shift completed"],
]);

const clipboardScore = z.object({
  id: z.number().positive().int(),
  shiftId: z.string().nullish(),
  workplaceId: z.string().nullish(),
  policyId: z.number().positive().int(),
  action: z.nativeEnum(ClipboardScoreActions),
  displayAction: z.string().nullish(),
  reason: z.string().nullish(),
  shiftStart: z.string().nullish(),
  targetArrivalTime: z.string().nullish(),
  clockInTime: z.string().nullish(),
  facilityName: z.string().nullish(),
  acceptedDispute: z.boolean().nullish(),
  scoreReversalReason: z.string().nullish(),
  createdAt: z.string(),
  clipboardScoreUpdatedScore: z.number().nullish(),
  clipboardScoreAdjustment: z.number().nullish(),
  clipboardScoreMultiplier: z.string(),
  leadTime: z.number().nullish(),
});

const clipboardScoreHistoryResponse = z.object({
  scoreLogs: z.array(clipboardScore),
});

export type ClipboardScoreHistoryResponse = z.infer<typeof clipboardScoreHistoryResponse>;
export type ClipboardScoreLog = z.infer<typeof clipboardScore>;

interface GetClipboardScoreHistoryParams {
  userId: string;
  filter?: { shiftIds?: string[] };
  pageSize?: number;
}

export function useGetClipboardScoreHistory(
  params: GetClipboardScoreHistoryParams,
  options: UseQueryOptions<ClipboardScoreHistoryResponse> = {}
): UseQueryResult<ClipboardScoreHistoryResponse> {
  const { userId, filter, pageSize } = params;
  const url = `${environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL}/scores/${userId}/history`;
  return useQuery({
    queryKey: [url],
    queryFn: async () => {
      const response = await get({
        url,
        responseSchema: clipboardScoreHistoryResponse,
        queryParams: { pageSize: pageSize ?? CLIPBOARD_SCORE_HISTORY_REQUEST_SIZE, filter },
      });

      const updatedScoreLogs = response.data.scoreLogs.filter((scoreLog) => {
        // We want to hide it only in mobile app. So we are filtering it out here.
        return (
          scoreLog.action !== "ORIENTATION_ACTIVATION" &&
          scoreLog.action !== "ORIENTATION_DEACTIVATION"
        );
      });

      return { ...response.data, scoreLogs: updatedScoreLogs };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_ATTENDANCE_SCORE_HISTORY,
    },
    ...options,
  });
}
